<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import ConversationsLabelAPI from 'dashboard/api/conversations';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

const PRAYER_COLUMNS = [
  {
    id: 'new',
    labelKey: 'MINISTRY.PRAYER_BOARD.COLUMNS.NEW',
    stageLabel: null,
    color: '#1B2A4A',
  },
  {
    id: 'praying',
    labelKey: 'MINISTRY.PRAYER_BOARD.COLUMNS.PRAYING',
    stageLabel: 'praying',
    color: '#C9A84C',
  },
  {
    id: 'answered',
    labelKey: 'MINISTRY.PRAYER_BOARD.COLUMNS.ANSWERED',
    stageLabel: 'prayer-answered',
    color: '#22C55E',
  },
  {
    id: 'follow-up',
    labelKey: 'MINISTRY.PRAYER_BOARD.COLUMNS.FOLLOW_UP',
    stageLabel: 'prayer-follow-up',
    color: '#4A7AB5',
  },
];

const STAGE_LABELS = PRAYER_COLUMNS.filter(c => c.stageLabel).map(
  c => c.stageLabel
);

const conversations = ref([]);
const isLoading = ref(false);
const movingId = ref(null);

const columnConversations = computed(() => {
  return PRAYER_COLUMNS.reduce((acc, column) => {
    if (column.stageLabel === null) {
      acc[column.id] = conversations.value.filter(conv => {
        const labels = conv.labels || [];
        return !STAGE_LABELS.some(sl => labels.includes(sl));
      });
    } else {
      acc[column.id] = conversations.value.filter(conv =>
        (conv.labels || []).includes(column.stageLabel)
      );
    }
    return acc;
  }, {});
});

const fetchPrayerRequests = async () => {
  isLoading.value = true;
  try {
    const { data } = await ConversationAPI.get({
      labels: ['prayer-request'],
      page: 1,
    });
    conversations.value = data.data?.payload || [];
  } catch {
    conversations.value = [];
  } finally {
    isLoading.value = false;
  }
};

const moveCard = async (conversation, toStageLabel) => {
  const currentLabels = conversation.labels || [];
  if (currentLabels.includes(toStageLabel)) return;
  movingId.value = conversation.id;
  const baseLabels = currentLabels.filter(l => !STAGE_LABELS.includes(l));
  const newLabels = toStageLabel ? [...baseLabels, toStageLabel] : baseLabels;
  try {
    await ConversationsLabelAPI.updateLabels(conversation.id, newLabels);
    await fetchPrayerRequests();
  } finally {
    movingId.value = null;
  }
};

const openConversation = conversation => {
  router.push({
    name: 'inbox_conversation',
    params: { accountId: accountId.value, conversation_id: conversation.id },
  });
};

const relativeDate = dateStr => {
  if (!dateStr) return '';
  const diff = Math.floor((Date.now() - new Date(dateStr * 1000)) / 60000);
  if (diff < 1) return 'Just now';
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
  return `${Math.floor(diff / 1440)}d ago`;
};

const getInitials = name => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(p => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

onMounted(fetchPrayerRequests);
</script>

<template>
  <div class="flex flex-col h-full bg-n-background overflow-hidden">
    <!-- Header -->
    <div
      class="flex items-center justify-between px-6 py-4 bg-woot-500 text-white shrink-0"
    >
      <div>
        <h1 class="text-xl font-semibold">
          {{ t('MINISTRY.PRAYER_BOARD.TITLE') }}
        </h1>
        <p class="text-sm text-white/70 mt-0.5">
          {{ t('MINISTRY.PRAYER_BOARD.DESCRIPTION') }}
        </p>
      </div>
      <button
        class="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-sm transition-colors"
        @click="fetchPrayerRequests"
      >
        <span
          class="i-lucide-refresh-cw size-4"
          :class="{ 'animate-spin': isLoading }"
        />
        {{ t('MINISTRY.PRAYER_BOARD.REFRESH') }}
      </button>
    </div>

    <!-- Board -->
    <div class="flex gap-4 p-4 overflow-x-auto h-full items-start">
      <div
        v-for="column in PRAYER_COLUMNS"
        :key="column.id"
        class="flex flex-col shrink-0 w-72 bg-n-slate-2 rounded-xl overflow-hidden"
      >
        <!-- Column header -->
        <div
          class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
        >
          <div class="flex items-center gap-2">
            <span
              class="size-2.5 rounded-full"
              :style="{ backgroundColor: column.color }"
            />
            <span class="text-sm font-semibold text-n-slate-12">{{
              t(column.labelKey)
            }}</span>
          </div>
          <span
            class="text-xs font-medium px-2 py-0.5 rounded-full text-white"
            :style="{ backgroundColor: column.color }"
          >
            {{ (columnConversations[column.id] || []).length }}
          </span>
        </div>

        <!-- Cards -->
        <div
          class="flex flex-col gap-2 p-2 overflow-y-auto max-h-[calc(100vh-220px)]"
        >
          <div
            v-if="(columnConversations[column.id] || []).length === 0"
            class="flex flex-col items-center justify-center py-8 text-center"
          >
            <span class="i-lucide-inbox size-8 text-n-slate-8 mb-2" />
            <p class="text-xs text-n-slate-9">
              {{ t('MINISTRY.PRAYER_BOARD.EMPTY') }}
            </p>
            <p
              v-if="conversations.length === 0"
              class="text-xs text-n-slate-8 mt-1 px-4"
            >
              {{ t('MINISTRY.PRAYER_BOARD.SETUP_HINT') }}
            </p>
          </div>

          <div
            v-for="conv in columnConversations[column.id] || []"
            :key="conv.id"
            class="bg-white dark:bg-n-slate-3 rounded-lg shadow-sm border border-n-weak p-3 cursor-pointer hover:shadow-md hover:border-woot-300 transition-all group"
            :class="{ 'opacity-50': movingId === conv.id }"
            @click="openConversation(conv)"
          >
            <!-- Contact name + priority -->
            <div class="flex items-start justify-between gap-2 mb-2">
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{
                  conv.meta?.sender?.name ||
                  t('MINISTRY.PRAYER_BOARD.CARD.UNKNOWN')
                }}
              </span>
              <span
                v-if="conv.priority"
                class="shrink-0 text-xs px-1.5 py-0.5 rounded font-medium"
                :class="{
                  'bg-red-100 text-red-600': conv.priority === 'urgent',
                  'bg-orange-100 text-orange-600': conv.priority === 'high',
                  'bg-yellow-100 text-yellow-600': conv.priority === 'medium',
                  'bg-slate-100 text-slate-500': conv.priority === 'low',
                }"
              >
                {{ conv.priority }}
              </span>
            </div>

            <!-- Message excerpt -->
            <p class="text-xs text-n-slate-10 line-clamp-2 mb-3">
              {{
                conv.last_non_activity_message?.content ||
                t('MINISTRY.PRAYER_BOARD.CARD.NO_MESSAGE')
              }}
            </p>

            <!-- Footer: agent + date + move -->
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-1.5">
                <div
                  v-if="conv.meta?.assignee"
                  class="size-5 rounded-full bg-woot-500 flex items-center justify-center text-white text-[10px] font-semibold"
                  :title="conv.meta.assignee.name"
                >
                  {{ getInitials(conv.meta.assignee.name) }}
                </div>
                <span v-else class="text-xs text-n-slate-8">{{
                  t('MINISTRY.PRAYER_BOARD.CARD.UNASSIGNED')
                }}</span>
              </div>
              <span class="text-xs text-n-slate-8">{{
                relativeDate(conv.created_at)
              }}</span>
            </div>

            <!-- Stage move buttons (visible on hover) -->
            <div
              class="mt-2 pt-2 border-t border-n-weak hidden group-hover:flex gap-1 flex-wrap"
              @click.stop
            >
              <button
                v-for="col in PRAYER_COLUMNS.filter(c => c.id !== column.id)"
                :key="col.id"
                class="text-xs px-2 py-0.5 rounded-full border transition-colors hover:text-white"
                :style="{ borderColor: col.color, color: col.color }"
                :class="{
                  'opacity-50 cursor-not-allowed': movingId === conv.id,
                }"
                :disabled="movingId === conv.id"
                @click="moveCard(conv, col.stageLabel)"
              >
                {{
                  t('MINISTRY.PRAYER_BOARD.MOVE_TO', { label: t(col.labelKey) })
                }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
